import { useMemo, useCallback, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useRenderValue } from '@hooks/useRenderValue'
import { getGlobalParams } from '@core/graphQL/globalParams'
import { emitCashOut } from '@sockets/socketEmitters'
import {
  resolveStake,
  resolveAutoCashoutInput,
  clampAutoCashoutValue,
} from '@core/betting/stakeEngine'
import { deriveInteractionControls } from '@core/betting/interactionControls'
import { placePanelBet, cancelPendingBet } from '@core/betting/classicBettingOrchestrator'
import {
  BET_STATE,
  BET_MODE,
  TOGGLE,
  AUTOBET_CYCLE,
  STAKE_CONTROL,
  CONTROL_DIRECTION,
  ACTION_VARIANT,
  AUTO_CASHOUT,
} from '@constants/bettingConstants'
import {
  hydrateDefaultStake,
  setStake,
  setMode,
  setAutoCashoutStatus,
  setAutoCashoutValue,
  setAutoBetCycle,
  markPending,
  selectPanel,
} from '@slices/bettingSlice'

const ACTION_LABEL = {
  [ACTION_VARIANT.BET]: 'Bet',
  [ACTION_VARIANT.START_AUTO]: 'Start AutoBet',
  [ACTION_VARIANT.CANCEL]: 'Cancel',
  [ACTION_VARIANT.PLACED]: 'Bet Placed',
  [ACTION_VARIANT.CASHOUT]: 'Cashout',
}

/**
 * The one hook the Classic cards consume. All logic lives here + the slice +
 * the pure logic layer; the card only renders what this returns. Pass the FE
 * panel id (PANELS.ONE | PANELS.TWO). Swapping layouts reuses this hook.
 */
export const useClassicBetting = (panel) => {
  const dispatch = useDispatch()
  const renderValue = useRenderValue()

  const p = useSelector(selectPanel(panel))
  const round = useSelector((state) => state.events.round)
  const stakeConfig = useSelector((state) => state.configurations.stakeConfigurations)
  const balance = useSelector((state) => state.wallet.balance)
  const currencyCode = useSelector((state) => state.wallet.displayCurrencyCode)
  const inputPrecisionMax = useSelector(
    (state) => state.conventions.currencyConfig?.inputPrecision?.max
  )

  const bounds = useMemo(
    () => ({
      min: stakeConfig.minimumStakeAmount,
      max: stakeConfig.maximumStakeAmount,
      step: stakeConfig.step,
      maxDecimals: inputPrecisionMax ?? 2,
    }),
    [stakeConfig, inputPrecisionMax]
  )

  // Seed the stake from the runtime config's defaultStakeAmount (or
  // minimumStakeAmount if absent). Re-runs whenever the config default changes —
  // so the real BE value (which arrives after the seed defaults) is applied even
  // if this panel mounted first. The slice only overwrites panels the player
  // hasn't manually edited, so it never clobbers a chosen stake.
  useEffect(() => {
    const seed = stakeConfig.defaultStakeAmount ?? stakeConfig.minimumStakeAmount
    if (seed != null) dispatch(hydrateDefaultStake({ stake: String(seed) }))
  }, [stakeConfig.defaultStakeAmount, stakeConfig.minimumStakeAmount, dispatch])

  const interactionControls = useMemo(
    () => deriveInteractionControls({ panel: p, round, bounds, balance }),
    [p, round, bounds, balance]
  )

  // The button the player sees, resolved once from the derived controls.
  const actionButton = useMemo(
    () => ({
      variant: interactionControls.actionVariant,
      label: ACTION_LABEL[interactionControls.actionVariant],
      enabled: interactionControls.actionButton === TOGGLE.ENABLED,
    }),
    [interactionControls.actionVariant, interactionControls.actionButton]
  )

  // Live climbing value while running; the settled payout once cashed out; else
  // null. DERIVED — never stored, never rewritten on every socket tick.
  const liveCashout = useMemo(() => {
    if (p.betState === BET_STATE.BET_RUNNING) {
      const m = Number(round.multiplier)
      return m ? Number(p.stake) * m : null
    }
    if (p.betState === BET_STATE.BET_CASHED_OUT) return p.cashoutAmount
    return null
  }, [p.betState, p.stake, p.cashoutAmount, round.multiplier])

  // ── stake control (the one global fn) ──────────────────────────────────────
  const controlStake = useCallback(
    (type, payload) => {
      const next = resolveStake({ type, current: p.stake, payload, bounds })
      dispatch(setStake({ panel, stake: next.value }))
      return next
    },
    [dispatch, panel, p.stake, bounds]
  )

  const incrementStake = useCallback(
    () => controlStake(STAKE_CONTROL.CONTROL_BUTTON, { direction: CONTROL_DIRECTION.INCREMENT }),
    [controlStake]
  )
  const decrementStake = useCallback(
    () => controlStake(STAKE_CONTROL.CONTROL_BUTTON, { direction: CONTROL_DIRECTION.DECREMENT }),
    [controlStake]
  )
  const setQuickStake = useCallback(
    (value) => controlStake(STAKE_CONTROL.QUICK_STAKE, { value }),
    [controlStake]
  )
  const onStakeInput = useCallback(
    (raw) => controlStake(STAKE_CONTROL.DIRECT_INPUT, { raw }),
    [controlStake]
  )

  // ── toggles ────────────────────────────────────────────────────────────────
  // The toggle only switches MANUAL<->AUTO. It does NOT place a bet — the loop
  // is armed by the "Start AutoBet" button. Switching back to MANUAL stops any
  // running loop and drops a still-pending bet (the controls lock it out entirely
  // once the bet has been submitted, so this can never discard a live bet).
  const toggleAutoBet = useCallback(() => {
    if (p.mode === BET_MODE.AUTO) {
      dispatch(setMode({ panel, mode: BET_MODE.MANUAL }))
      dispatch(setAutoBetCycle({ panel, cycle: AUTOBET_CYCLE.IDLE }))
      cancelPendingBet(panel)
    } else {
      dispatch(setMode({ panel, mode: BET_MODE.AUTO }))
    }
  }, [dispatch, panel, p.mode])

  const toggleAutoCashout = useCallback(() => {
    const next = p.autoCashout.status === TOGGLE.ENABLED ? TOGGLE.DISABLED : TOGGLE.ENABLED
    dispatch(setAutoCashoutStatus({ panel, status: next }))
  }, [dispatch, panel, p.autoCashout.status])

  // Live typing: sanitize to 2 decimals + snap down above MAX. Min is enforced
  // on blur so the player can still type through "1" on the way to "1.01".
  const onAutoCashoutInput = useCallback(
    (raw) => {
      dispatch(
        setAutoCashoutValue({
          panel,
          value: resolveAutoCashoutInput(raw, {
            max: AUTO_CASHOUT.MAX,
            maxDecimals: AUTO_CASHOUT.DECIMALS,
          }),
        })
      )
    },
    [dispatch, panel]
  )

  // On blur, clamp any typed value into [MIN, MAX] so the field only ever
  // settles on an accepted multiplier. An empty field is left empty.
  const onAutoCashoutBlur = useCallback(() => {
    const raw = p.autoCashout.value
    if (raw == null || raw === '' || raw === '.') return
    const clamped = clampAutoCashoutValue(raw, {
      min: AUTO_CASHOUT.MIN,
      max: AUTO_CASHOUT.MAX,
      maxDecimals: AUTO_CASHOUT.DECIMALS,
    })
    if (clamped !== raw) dispatch(setAutoCashoutValue({ panel, value: clamped }))
  }, [dispatch, panel, p.autoCashout.value])

  // ── the smart action button ─────────────────────────────────────────────────
  // Pressing Bet does NOT contact the server: it stores a pending bet, which the
  // submission scheduler sends shortly before the round takes off (or immediately,
  // if the press already lands inside that window). Bet/Cancel both go through the
  // orchestrator, which re-checks live state — this component's `p` is a render
  // snapshot and can be a frame behind the submission.
  const onActionButton = useCallback(() => {
    if (!actionButton.enabled) return
    switch (actionButton.variant) {
      case ACTION_VARIANT.BET: {
        placePanelBet(panel)
        break
      }
      case ACTION_VARIANT.START_AUTO: {
        // arm the loop, then queue this round's bet
        dispatch(setAutoBetCycle({ panel, cycle: AUTOBET_CYCLE.ACTIVE }))
        placePanelBet(panel)
        break
      }
      case ACTION_VARIANT.CANCEL: {
        // Only ever discards a PENDING bet and/or stops the auto loop. A pending
        // bet exists solely in frontend state, so this is a local clear with no
        // server call; once a bet is submitted it is a no-op.
        if (p.mode === BET_MODE.AUTO) {
          dispatch(setAutoBetCycle({ panel, cycle: AUTOBET_CYCLE.IDLE }))
        }
        cancelPendingBet(panel)
        break
      }
      case ACTION_VARIANT.CASHOUT: {
        if (p.transactionId) {
          const { gameCode } = getGlobalParams()
          dispatch(markPending({ panel }))
          emitCashOut({ transactionId: p.transactionId, gameCode })
        }
        break
      }
      default:
        break
    }
  }, [actionButton, dispatch, panel, p.transactionId, p.mode])

  return {
    // state
    stake: p.stake,
    mode: p.mode,
    betState: p.betState,
    pending: p.pending,
    autoCashout: p.autoCashout,
    autoBet: p.autoBet,
    transactionId: p.transactionId,
    cashoutAmount: p.cashoutAmount,
    // derived
    interactionControls,
    liveCashout,
    actionButton,
    // config + formatting
    presetStakes: stakeConfig.presetStakes,
    bounds,
    renderValue,
    currencyCode,
    // actions
    controlStake,
    incrementStake,
    decrementStake,
    setQuickStake,
    onStakeInput,
    toggleAutoBet,
    toggleAutoCashout,
    onAutoCashoutInput,
    onAutoCashoutBlur,
    onActionButton,
  }
}
